import { create } from 'zustand';
import { TenantId, TENANT_PRESETS, isTenantId, resolveTenantId } from '../env/tenantConfig';

const TENANT_STORAGE_KEY = 'active_tenant_id';

function getInitialTenant(): TenantId {
  // 1. Check query param if present in current URL
  if (typeof window !== 'undefined' && window.location) {
    const params = new URLSearchParams(window.location.search);
    const queryTenant = params.get('tenant') || params.get('tenantId');
    if (isTenantId(queryTenant)) {
      localStorage.setItem(TENANT_STORAGE_KEY, queryTenant);
      return queryTenant;
    }
  }

  // 2. Check localStorage
  if (typeof window !== 'undefined' && window.localStorage) {
    const stored = localStorage.getItem(TENANT_STORAGE_KEY);
    if (isTenantId(stored)) {
      return stored;
    }
  }

  // 3. Fallback to curvelearning
  return resolveTenantId();
}

function checkInitialLoggedIn(tenantId: TenantId): boolean {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  const token = localStorage.getItem(`${tenantId}:internal_token`) || localStorage.getItem('internal_token');
  const timestamp = localStorage.getItem(`${tenantId}:token_validated_at`) || localStorage.getItem('token_validated_at');
  if (!token || !timestamp) return false;
  return Date.now() - parseInt(timestamp, 10) < 60 * 60 * 1000;
}

interface TenantState {
  tenantId: TenantId;
  isLoggedIn: boolean;
  setTenantId: (tenantId: TenantId) => void;
  setIsLoggedIn: (isLoggedIn: boolean) => void;
}

const initialTenant = getInitialTenant();

export const useTenantStore = create<TenantState>((set) => ({
  tenantId: initialTenant,
  isLoggedIn: checkInitialLoggedIn(initialTenant),
  setTenantId: (tenantId: TenantId) => {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem(TENANT_STORAGE_KEY, tenantId);
    }
    set({ tenantId, isLoggedIn: checkInitialLoggedIn(tenantId) });
  },
  setIsLoggedIn: (isLoggedIn: boolean) => {
    set({ isLoggedIn });
  },
}));

export const getActiveTenantId = (): TenantId => {
  return useTenantStore.getState().tenantId;
};

export const getActiveTenantPreset = () => {
  const tenantId = getActiveTenantId();
  return TENANT_PRESETS[tenantId] || TENANT_PRESETS.curvelearning;
};
